import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Output, EventEmitter, Input } from '@angular/core';

import { ViewRenderer } from '@unpispas/upp-wdgt';
import { UppKonvaModel } from '@unpispas/upp-konva';
import { UppKonvaShape } from '@unpispas/upp-konva';

import type { UppKonvaOrientation } from '@unpispas/upp-konva';

/** Mobile canvas pointer tool: select/edit shapes vs pan/zoom navigation. */
export type UppFloorCanvastool = 'select' | 'navigate';

/** Zoom/pan payload forwarded from `upp-konva` VIEW navigation. */
export type UppViewTransform = {
    readonly zoomFactor: number;
    readonly panOffsetXPx: number;
    readonly panOffsetYPx: number;
};

/************************************/
/* FLOOR KONVA PANEL (HOST)         */
/************************************/

@Component({
    selector: 'upp-floor-render',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-render.html',
    styleUrls: ['./upp-render.scss'],
    providers: [{
        provide: ViewRenderer,
        useFactory: (cd: ChangeDetectorRef) => new ViewRenderer(cd),
        deps: [ChangeDetectorRef],
    }],
})
export class UppFloorRenderComponent implements OnInit, OnDestroy, OnChanges {
    /***********************************************/
    /* INPUTS AND OUTPUTS                          */
    /***********************************************/

    @Input() orientation: UppKonvaOrientation = 'AUTO';
    @Input() canvastool: UppFloorCanvastool | null = null;

    @Output() readonly OnKonva = new EventEmitter<UppKonvaModel | null>();
    @Output() readonly OnSelect = new EventEmitter<readonly UppKonvaShape[]>();
    @Output() readonly OnTransform = new EventEmitter<UppViewTransform>();

    /***********************************************/
    /* DIRECT GETTERS USED BY THE UI             */
    /***********************************************/

    private _konva: UppKonvaModel | null = null;
    get Konva(): UppKonvaModel | null {
        return this._konva;
    }

    set Konva(konva: UppKonvaModel | null) {
        this._konva = konva;
        this.OnKonva.emit(konva);
    }

    /***********************************************/
    /* CONSTRUCTOR AND LIFECYCLE                   */
    /***********************************************/

    constructor(
        private readonly _change: ViewRenderer,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['orientation']) {
            this._syncOrientation();
        }

        if (changes['canvastool']) {
            this._syncCanvastool();
        }
    }

    ngOnInit(): void {
        this.Konva = new UppKonvaModel({
            name: '',
            width: 0,
            height: 0,
            shapes: [],
        });

        this._syncOrientation();
        this._syncCanvastool();
        this._change.markForCheck();
    }

    ngOnDestroy(): void {
        if (this.Konva) {
            this.Konva.doDestroy();
            this.Konva = null;
        }
    }

    OnKonvaSelect(shapes: readonly UppKonvaShape[]): void {
        this.OnSelect.emit(shapes);
    }

    OnKonvaTransform(payload: UppViewTransform): void {
        this.OnTransform.emit(payload);
    }

    private _syncOrientation(): void {
        if (!this._konva?.area) {
            return;
        }

        const nxt = this.orientation ?? 'AUTO';
        const src = this._konva.area.source;
        const cur = (src.orientation ?? 'AUTO') as UppKonvaOrientation;

        if (cur === nxt) {
            return;
        }

        this._konva.applyAreaFromSource({ ...src, orientation: nxt });
    }

    private _syncCanvastool(): void {
        if (!this._konva || this.canvastool === null) {
            return;
        }

        this._konva.mode = this.canvastool === 'navigate' ? 'VIEW' : 'EDIT';
    }
}
